import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { HoverCloseTimer } from '../../hover-close-timer';
import { TimerTools } from '../../../tools/timer-tools';
import { BrowserTools } from '../../../tools/browser-tools';
import { PopoverService, SolPopover } from '../../../services/popover.service';
import { HostStyle, SolPopoverState } from './sol-popover.state';

@Component({
  selector: 'sol-popover',
  imports: [],
  templateUrl: './sol-popover.component.html',
  styles: [
    `
      :host {
        position: relative;
      }
    `,
  ],
})
export class SolPopoverComponent extends AbstractComponent<SolPopoverState> implements AfterViewInit, OnDestroy {
  // The Vue host renders its HubSpot chat launcher over the bottom-left corner of the embedded app.
  private static readonly EMBEDDED_CHAT_LAUNCHER_RESERVE = 88;

  // Dependencies
  private readonly popoverService = inject(PopoverService);

  // Computed
  protected readonly hostStyle$ = this.compute(state => state.hostStyle);
  protected readonly fixed$ = this.compute(state => state.fixed);
  protected readonly containerClass$ = this.compute(state => state.containerClass);
  protected readonly popoverClass$ = this.compute(state => state.popoverClass);
  protected readonly popoverVisible$ = this.compute(state => state.popoverVisible);
  protected readonly popoverStyle$ = this.compute(state => state.popoverStyle);
  protected readonly contentStyle$ = this.compute(state => state.contentStyle);

  // Binding
  @ViewChild('container')
  private containerRef?: ElementRef;
  @ViewChild('popoverContent', { read: ViewContainerRef })
  private popoverContentRef?: ViewContainerRef;
  private resizeObserver?: ResizeObserver;

  // Cache
  private readonly hoverClose = new HoverCloseTimer();
  private hoverParentElement?: HTMLElement;
  private readonly onParentMouseEnter = () => this.update(state => state.withParentHover(true));
  private readonly onParentMouseLeave = () => this.update(state => state.withParentHover(false));

  constructor() {
    super(SolPopoverState.of(BrowserTools.isEmbedded() ? SolPopoverComponent.EMBEDDED_CHAT_LAUNCHER_RESERVE : 0));

    this.watchComputed(
      state => state.popoverVisible,
      popoverVisible => {
        if (popoverVisible) {
          TimerTools.nextTick(() => this.observeResize());
        } else {
          this.cleanUpResize();
        }
      },
    );
    this.watchComputed(
      state => state.data?.loadComponent,
      () => this.updatePopoverComponent(),
    );
    this.watchComputed(
      state => state.data,
      data => this.updateHoverParent(data),
    );
    this.watchComputed(
      state => state.hoverOut,
      hoverOut => {
        if (hoverOut) {
          this.scheduleHoverOutClose();
        } else {
          this.hoverClose.cancel();
        }
      },
    );
  }

  @HostBinding('style')
  public get hostStyle(): HostStyle {
    return this.hostStyle$();
  }

  ngAfterViewInit(): void {
    this.updatePopoverComponent();
  }

  ngOnDestroy(): void {
    this.cleanUpResize();
    this.hoverClose.cancel();
    this.detachHoverParent();
  }

  @Input()
  public set data(data: SolPopover<any> | undefined) {
    this.update(state => state.withData(data));
  }

  protected onMouseEnter() {
    this.update(state => state.withHover(true));
  }

  protected onMouseLeave() {
    this.update(state => state.withHover(false));
  }

  private updateHoverParent(data: SolPopover<any> | undefined): void {
    this.detachHoverParent();
    const parentRef = data?.options.closeOnHoverOut ? data.options.boundsRef : undefined;
    if (!parentRef) {
      return;
    }
    const element = parentRef.nativeElement;
    element.addEventListener('mouseenter', this.onParentMouseEnter);
    element.addEventListener('mouseleave', this.onParentMouseLeave);
    this.hoverParentElement = element;
    this.update(state => state.withParentHover(element.matches(':hover')));
  }

  private detachHoverParent(): void {
    this.hoverParentElement?.removeEventListener('mouseenter', this.onParentMouseEnter);
    this.hoverParentElement?.removeEventListener('mouseleave', this.onParentMouseLeave);
    this.hoverParentElement = undefined;
  }

  private scheduleHoverOutClose(): void {
    this.hoverClose.schedule(() => {
      const data = this.state.data;
      if (data) {
        this.popoverService.close(data.id);
      }
    });
  }

  private observeResize() {
    if (this.containerRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.containerRef?.nativeElement) {
          return;
        }
        const popoverRect = this.containerRef!.nativeElement.getBoundingClientRect();

        this.update(state =>
          state.withResize(
            popoverRect.width,
            popoverRect.height,
            document.documentElement.clientWidth,
            document.documentElement.clientHeight,
          ),
        );
      });
      this.resizeObserver.observe(this.containerRef.nativeElement);
    }
  }

  private cleanUpResize() {
    this.resizeObserver?.disconnect();
  }

  /**
   * Dynamically updates the popover components inside their containers.
   */
  private updatePopoverComponent(): void {
    // No ref or data
    const data = this.state.data;
    if (!data || !this.popoverContentRef) {
      return;
    }

    // Already exists?
    if (this.popoverContentRef.length > 0) {
      return;
    }

    data.loadComponent.then(componentType => {
      const componentRef: ComponentRef<AbstractComponent<any>> = this.popoverContentRef!.createComponent(componentType);
      data.configurer(componentRef, data.id);
      PopoverService.registerInstance(data.id, componentRef.instance);
      TimerTools.nextTick(() => componentRef.instance.focus());
    });
  }
}
